import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import useLenis from "@/hooks/useLenis";
import Layout from "@/components/Layout";

/**
 * /reel — dedicated cinema pass-through for the 2:20 master reel.
 * Founder plain-ink 2026-08-15 (placement pick B): full-bleed 16:9,
 * single video, muted autoplay, click-to-unmute. Shorts live on social;
 * the master lives here on the storefront as the anchor.
 */
export default function Reel() {
  useLenis();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <Layout>
      <div data-testid="page-reel" className="relative bg-black text-[#ededed] min-h-screen">
        {/* Cinema stage */}
        <section
          data-testid="reel-stage"
          className="relative w-full flex items-center justify-center bg-black"
          style={{ minHeight: "calc(100svh - 104px)" }}
        >
          <div className="relative w-full max-w-[1760px] aspect-video mx-auto shadow-[0_40px_120px_-20px_rgba(255,90,0,0.25)]">
            <video
              ref={videoRef}
              data-testid="reel-video"
              src="/samples/ad-campaign/master_v2_2m20.mp4"
              poster="/samples/ad-campaign/master_v2_poster.jpg"
              autoPlay
              muted
              playsInline
              controls
              preload="auto"
              className="w-full h-full object-contain bg-black"
            />
            {/* Muted-state hint (dismisses on click) */}
            {muted && (
              <button
                onClick={toggleMute}
                data-testid="reel-unmute"
                className="absolute top-4 right-4 z-10 group flex items-center gap-2 bg-black/70 backdrop-blur-md border border-[#ff5a00]/40 text-[#ff5a00] hover:bg-[#ff5a00] hover:text-black transition-colors px-4 py-2 font-mono text-[10px] uppercase tracking-[0.32em]"
                aria-label="Unmute reel"
              >
                <span className="text-base leading-none">🔊</span>
                <span>Tap to unmute</span>
              </button>
            )}
          </div>
        </section>

        {/* Footer — orient the visitor after the reel */}
        <section
          data-testid="reel-footer"
          className="relative border-t border-white/[0.05] bg-[#050505]"
        >
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16 sm:py-20 grid gap-10 md:grid-cols-[1.4fr,1fr] items-start">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="font-mono text-[10px] uppercase tracking-[0.45em] text-[#ff5a00]"
              >
                The Reel · 2:20
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                data-testid="reel-headline"
                className="font-display uppercase leading-[0.95] text-[#ededed] mt-4"
                style={{ fontSize: "clamp(2.25rem, 6vw, 4.75rem)", letterSpacing: "-0.005em" }}
              >
                Twelve worlds. One forge.
                <br />
                <span className="italic text-[#ff5a00]">And a sibling next door.</span>
              </motion.h1>
            </div>

            <div className="space-y-4">
              <Link
                to="/products"
                data-testid="reel-cta-products"
                className="block bg-[#ff5a00] text-black font-mono text-[11px] uppercase tracking-[0.32em] px-6 py-4 hover:bg-[#ff8f00] transition-colors"
              >
                See the ten product lines →
              </Link>
              <Link
                to="/what-we-collapse"
                data-testid="reel-cta-collapse"
                className="block border border-[#27272a] hover:border-[#ff5a00]/60 font-mono text-[11px] uppercase tracking-[0.32em] text-[#ededed] px-6 py-4 transition-colors"
              >
                What we collapse →
              </Link>
              <Link
                to="/philanthropy"
                data-testid="reel-cta-philanthropy"
                className="block border border-[#27272a] hover:border-[#ff5a00]/60 font-mono text-[11px] uppercase tracking-[0.32em] text-[#ededed] px-6 py-4 transition-colors"
              >
                The pledge on record →
              </Link>
              <Link
                to="/mission"
                data-testid="reel-cta-mission"
                className="block border border-[#27272a] hover:border-[#ff5a00]/60 font-mono text-[11px] uppercase tracking-[0.32em] text-[#ededed] px-6 py-4 transition-colors"
              >
                Read the mission →
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
